import { useEffect, useState } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { shortConcept } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCheck } from "lucide-react";
import { toast } from "sonner";

export function ManualDialog({ tx, open, onClose, onDone }) {
  const [payables, setPayables] = useState([]);
  const [allocs, setAllocs] = useState({});
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/payables").then((r) => setPayables(r.data.items)).catch(() => {});
      setAllocs({});
    }
  }, [open]);

  const [fxAmounts, setFxAmounts] = useState({});
  if (!tx) return null;
  const target = Math.abs(tx.amount || 0);
  const allocTotal = Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0);
  const diff = Math.round((target - allocTotal) * 100) / 100;

  const toggle = (inv) => {
    const next = { ...allocs };
    if (next[inv.invoice_id] !== undefined) delete next[inv.invoice_id];
    else {
      next[inv.invoice_id] = inv.pending;
      if (inv.currency && inv.currency !== "EUR") setFxAmounts((p) => ({ ...p, [inv.invoice_id]: inv.pending }));
    }
    setAllocs(next);
  };

  const confirm = async () => {
    try {
      await api.post("/reconciliation/manual", {
        transaction_id: tx.id,
        allocations: Object.entries(allocs).map(([invoice_id, amount]) => ({
          invoice_id, amount: Number(amount),
          invoice_amount: fxAmounts[invoice_id] != null ? Number(fxAmounts[invoice_id]) : undefined,
        })),
      });
      toast.success("Conciliación manual registrada");
      onDone();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al conciliar");
    }
  };

  const filtered = payables.filter((i) =>
    (i.pending ?? 0) > 0.005 && (!search || (i.supplier_name || "").toLowerCase().includes(search.toLowerCase())
    || (i.invoice_number || "").toLowerCase().includes(search.toLowerCase())));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manual-recon-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Conciliar manualmente</DialogTitle>
          <DialogDescription>
            Movimiento: {shortConcept(tx.concept)} · <span className="font-semibold">{fmtDate(tx.date)}</span> · <span className="font-num font-semibold">{fmtEUR(target)}</span>.
            Selecciona una o varias facturas — los importes deben sumar exactamente el movimiento.
          </DialogDescription>
        </DialogHeader>
        <Input data-testid="manual-recon-search" placeholder="Buscar proveedor o factura..." value={search}
          onChange={(e) => setSearch(e.target.value)} className="h-9" />
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {filtered.map((inv) => (
            <div key={inv.invoice_id} data-testid={`manual-inv-${inv.invoice_id}`}
              className={`flex items-center gap-3 border rounded-lg px-3 py-2 cursor-pointer transition-colors ${allocs[inv.invoice_id] !== undefined ? "border-[#D9381E] bg-red-50/40" : "hover:bg-slate-50"}`}
              onClick={() => toggle(inv)}>
              <input type="checkbox" readOnly checked={allocs[inv.invoice_id] !== undefined} className="accent-[#D9381E]" />
              <div className="flex-1 min-w-0 text-sm">
                <span className="font-medium">{inv.supplier_name}</span>
                <span className="text-slate-400 ml-2 font-num text-xs">{inv.invoice_number || "s/n"} · {fmtDate(inv.invoice_date)}</span>
              </div>
              {allocs[inv.invoice_id] !== undefined ? (
                <div className="flex flex-col items-end gap-1">
                  <Input type="number" step="0.01" data-testid={`manual-amount-${inv.invoice_id}`} className="w-28 h-8 font-num" value={allocs[inv.invoice_id]}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setAllocs({ ...allocs, [inv.invoice_id]: e.target.value })} />
                  {inv.currency && inv.currency !== "EUR" && (
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Input type="number" step="0.01" data-testid={`manual-fx-${inv.invoice_id}`} className="w-24 h-7 font-num text-xs"
                        value={fxAmounts[inv.invoice_id] ?? inv.pending}
                        onChange={(e) => setFxAmounts({ ...fxAmounts, [inv.invoice_id]: e.target.value })} />
                      <span className="text-xs text-slate-400 whitespace-nowrap">{inv.currency} factura</span>
                    </div>
                  )}
                </div>
              ) : (
                <span className="font-num text-sm">{fmtEUR(inv.pending)}</span>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between border-t pt-3">
          <div className="text-sm">
            Asignado: <span className="font-num font-semibold">{fmtEUR(allocTotal)}</span>
            <span className={`ml-3 font-num text-xs ${Math.abs(diff) < 0.01 ? "text-green-700" : "text-red-600"}`}>
              {Math.abs(diff) < 0.01 ? "Cuadra ✓" : `Diferencia: ${fmtEUR(diff)}`}
            </span>
          </div>
          <Button data-testid="manual-recon-confirm" onClick={confirm} disabled={Math.abs(diff) >= 0.01 || Object.keys(allocs).length === 0}
            className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
            <CheckCheck className="w-4 h-4" /> Conciliar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
